import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API_ENDPOINTS } from './config';
import {
  FiUploadCloud, FiDownload, FiCheck, FiX, FiAlertTriangle,
  FiMapPin, FiClock, FiCalendar, FiCamera, FiMail, FiShield,
  FiRefreshCw, FiLayout, FiArrowLeft, FiUsers
} from 'react-icons/fi';

const baseUrl = API_ENDPOINTS.LOGIN.split('/auth')[0];

const STEPS = {
  CONFIGURE: 1,
  UPLOAD_CSV: 2,
  PREVIEW: 3,
  PROGRESS: 4,
  DONE: 5,
};

function BulkRequest({ onExit, preSelectedOrgId }) {
  const userRoles = JSON.parse(localStorage.getItem('roles') || '[]');
  const isSuperAdmin = userRoles.includes('SUPER_ADMIN');
  const userOrgId = localStorage.getItem('orgId');
  const targetOrgId = isSuperAdmin ? preSelectedOrgId : userOrgId;

  const [step, setStep] = useState(STEPS.CONFIGURE);

  // Step 1: Configuration
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [fieldValues, setFieldValues] = useState({});
  const [captureLocation, setCaptureLocation] = useState(false);
  const [enableReminder, setEnableReminder] = useState(false);
  const [reminderDuration, setReminderDuration] = useState('24');
  const [enableExpiry, setEnableExpiry] = useState(false);
  const [expiryDate, setExpiryDate] = useState('');
  const [captureFace, setCaptureFace] = useState(false);
  const [emailNotificationEnabled, setEmailNotificationEnabled] = useState(true);
  const [otpEnabled, setOtpEnabled] = useState(false);
  const [documentName, setDocumentName] = useState('');

  // Step 2: CSV upload
  const [csvFile, setCsvFile] = useState(null);
  const [parsedRows, setParsedRows] = useState([]);
  const [parseError, setParseError] = useState('');
  const fileInputRef = useRef(null);

  // Step 4/5: Progress
  const [batchId, setBatchId] = useState(null);
  const [batchStatus, setBatchStatus] = useState(null);
  const pollRef = useRef(null);

  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${baseUrl}/templates/list`)
      .then(res => setTemplates(res.data || []))
      .catch(() => {});
  }, []);

  // Poll batch progress
  const pollStatus = useCallback(() => {
    if (!batchId) return;
    axios.get(`${baseUrl}/requests/bulk_status/${batchId}`)
      .then(res => {
        setBatchStatus(res.data);
        if (res.data.status === 'completed') {
          clearInterval(pollRef.current);
          setStep(STEPS.DONE);
        }
      })
      .catch(() => {});
  }, [batchId]);

  useEffect(() => {
    if (step === STEPS.PROGRESS && batchId) {
      pollRef.current = setInterval(pollStatus, 2000);
      pollStatus();
    }
    return () => clearInterval(pollRef.current);
  }, [step, batchId, pollStatus]);

  const handleTemplateSelect = (e) => {
    const tId = e.target.value;
    if (!tId) { setSelectedTemplate(null); setFieldValues({}); return; }
    const tmpl = templates.find(t => t._id === tId);
    setSelectedTemplate(tmpl || null);
    const initFields = {};
    tmpl?.text_fields?.forEach(f => { initFields[f.label] = ''; });
    setFieldValues(initFields);
    setDocumentName(tmpl?.title || '');
  };

  const downloadCsvTemplate = async () => {
    const params = selectedTemplate ? `?template_id=${selectedTemplate._id}` : '';
    try {
      const res = await axios.get(`${baseUrl}/requests/bulk_template_csv${params}`, {
        responseType: 'blob'
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'batch_send_template.csv';
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download CSV template', err);
    }
  };

  const parseClientSideCsv = (text) => {
    const lines = text.split(/\r?\n/).filter(l => l.trim());
    if (lines.length < 2) throw new Error('CSV must have a header row and at least one data row');
    const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
    const rows = [];
    for (let i = 1; i < lines.length; i++) {
      const vals = lines[i].split(',').map(v => v.trim().replace(/^"|"$/g, ''));
      const row = {};
      headers.forEach((h, idx) => { row[h] = vals[idx] || ''; });
      rows.push(row);
    }
    return rows;
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setParseError('');
    setCsvFile(file);
    setParsedRows([]);

    const ext = file.name.split('.').pop().toLowerCase();
    if (ext === 'csv') {
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          const rows = parseClientSideCsv(ev.target.result);
          setParsedRows(rows);
        } catch (err) {
          setParseError(err.message);
        }
      };
      reader.readAsText(file);
    } else if (ext === 'xlsx' || ext === 'xls') {
      // For xlsx, preview will be done on server - just show filename
      setParsedRows([{ _note: 'Excel file preview available after submission' }]);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      const dt = new DataTransfer();
      dt.items.add(file);
      fileInputRef.current.files = dt.files;
      handleFileChange({ target: { files: [file] } });
    }
  };

  const handleSubmit = async () => {
    setErrorMessage('');
    if (!selectedTemplate) {
      setErrorMessage('Please select a template to use for batch send.');
      return;
    }
    if (!csvFile) {
      setErrorMessage('Please upload a CSV or Excel file with recipient data.');
      return;
    }
    if (enableExpiry && !expiryDate) {
      setErrorMessage('Please select an expiry date.');
      return;
    }

    setIsSubmitting(true);
    const config = {
      template_id: selectedTemplate._id,
      orgId: targetOrgId,
      document_name: documentName || selectedTemplate.title,
      field_values: fieldValues,
      security_settings: {
        capture_location: captureLocation,
        reminder_enabled: enableReminder,
        reminder_duration: enableReminder ? reminderDuration : null,
        expiry_enabled: enableExpiry,
        expiry_date: enableExpiry ? expiryDate : null,
        face_capture: captureFace,
        email_notification_enabled: emailNotificationEnabled,
        otp_enabled: otpEnabled,
      },
    };

    const formData = new FormData();
    formData.append('data', JSON.stringify(config));
    formData.append('file', csvFile);

    try {
      const res = await axios.post(`${baseUrl}/requests/bulk_create`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setBatchId(res.data.batch_id);
      setBatchStatus({ total: res.data.total, processed: 0, success: 0, failed: 0, errors: [], status: 'processing' });
      setStep(STEPS.PROGRESS);
    } catch (err) {
      setErrorMessage(err.response?.data?.error || 'Failed to start batch send. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const progressPct = batchStatus
    ? Math.round((batchStatus.processed / (batchStatus.total || 1)) * 100)
    : 0;

  // ── Styles ──────────────────────────────────────────────────────────────────
  const styles = `
    .bulk-container { max-width: 900px; margin: 0 auto; }
    .bulk-step-indicator {
      display: flex; gap: 0; margin-bottom: 32px; border-radius: 10px;
      overflow: hidden; border: 1px solid var(--black-border);
    }
    .bulk-step-item {
      flex: 1; padding: 10px 16px; text-align: center; font-size: 13px;
      font-weight: 600; background: var(--black-bg-card-2);
      color: var(--text-on-black-muted); border-right: 1px solid var(--black-border);
      transition: all 0.2s;
    }
    .bulk-step-item:last-child { border-right: none; }
    .bulk-step-item.active { background: var(--primary); color: #000; }
    .bulk-step-item.done { background: rgba(16,185,129,0.15); color: #10B981; }

    .bulk-card {
      background: var(--black-bg-card-2); border: 1px solid var(--black-border);
      border-radius: 12px; padding: 28px; margin-bottom: 20px;
    }
    .bulk-card h3 { margin: 0 0 20px 0; color: var(--text-on-black); font-size: 16px; font-weight: 700; }

    .bulk-switch-row {
      display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 20px;
    }
    .bulk-switch-container {
      display: flex; align-items: center; gap: 10px;
      padding: 10px 14px; background: var(--black-bg-card-3);
      border-radius: 8px; border: 1px solid var(--black-border);
    }
    .bulk-switch { position: relative; display: inline-block; width: 40px; height: 22px; flex-shrink: 0; }
    .bulk-switch input { opacity: 0; width: 0; height: 0; }
    .bulk-slider {
      position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0;
      background-color: var(--black-border-light); transition: .3s; border-radius: 22px;
    }
    .bulk-slider:before {
      position: absolute; content: ""; height: 16px; width: 16px;
      left: 3px; bottom: 3px; background: var(--text-on-black);
      transition: .3s; border-radius: 50%;
    }
    .bulk-switch input:checked + .bulk-slider { background-color: var(--primary); }
    .bulk-switch input:checked + .bulk-slider:before { transform: translateX(18px); }
    .bulk-config-label {
      font-size: 13px; font-weight: 500; color: var(--text-on-black-secondary);
      display: flex; align-items: center; gap: 6px; white-space: nowrap;
    }

    .bulk-drop-zone {
      border: 2px dashed var(--black-border-light); border-radius: 10px;
      padding: 48px 24px; text-align: center; cursor: pointer;
      background: var(--black-bg-card-3); transition: all 0.2s;
      color: var(--text-on-black-muted);
    }
    .bulk-drop-zone:hover, .bulk-drop-zone.drag-over {
      border-color: var(--primary); background: rgba(255,214,0,0.06);
    }
    .bulk-drop-zone.has-file { border-style: solid; border-color: #10B981; background: rgba(16,185,129,0.06); }

    .bulk-preview-table {
      width: 100%; border-collapse: collapse; font-size: 13px; margin-top: 16px;
    }
    .bulk-preview-table th {
      background: var(--black-bg-card-3); color: var(--text-on-black-muted);
      padding: 8px 12px; text-align: left; font-weight: 600; font-size: 11px;
      text-transform: uppercase; border-bottom: 1px solid var(--black-border);
    }
    .bulk-preview-table td {
      padding: 8px 12px; color: var(--text-on-black-secondary);
      border-bottom: 1px solid var(--black-border);
    }
    .bulk-preview-table tr:last-child td { border-bottom: none; }
    .bulk-preview-table tr:hover td { background: var(--black-bg-card-3); }

    .bulk-progress-bar-track {
      width: 100%; height: 10px; background: var(--black-bg-card-3);
      border-radius: 10px; overflow: hidden; margin: 12px 0;
    }
    .bulk-progress-bar-fill {
      height: 100%; background: var(--primary);
      border-radius: 10px; transition: width 0.4s ease;
    }
    .bulk-stat { display: flex; gap: 20px; flex-wrap: wrap; margin-top: 16px; }
    .bulk-stat-item {
      flex: 1; min-width: 100px; padding: 16px; border-radius: 8px;
      background: var(--black-bg-card-3); border: 1px solid var(--black-border);
      text-align: center;
    }
    .bulk-stat-num { font-size: 28px; font-weight: 700; }
    .bulk-stat-label { font-size: 12px; color: var(--text-on-black-muted); margin-top: 4px; }

    .bulk-error-list {
      max-height: 200px; overflow-y: auto; margin-top: 16px;
      background: rgba(239,68,68,0.08); border: 1px solid rgba(239,68,68,0.3);
      border-radius: 8px; padding: 12px;
    }
    .bulk-error-row { font-size: 12px; color: #f87171; padding: 4px 0; border-bottom: 1px solid rgba(239,68,68,0.15); }
    .bulk-error-row:last-child { border-bottom: none; }

    .bulk-input {
      width: 100%; padding: 10px 12px; border-radius: 6px;
      border: 1px solid var(--black-border-light); background: var(--black-bg-card-3);
      color: var(--text-on-black); font-size: 14px; box-sizing: border-box;
    }
    .bulk-select { width: 100%; }
    .bulk-action-row { display: flex; gap: 12px; justify-content: flex-end; margin-top: 24px; }
  `;

  const SwitchToggle = ({ checked, onChange, label, icon }) => (
    <div className="bulk-switch-container">
      <label className="bulk-switch">
        <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} />
        <span className="bulk-slider"></span>
      </label>
      <span className="bulk-config-label">{icon} {label}</span>
    </div>
  );

  const stepLabels = ['Configure', 'Upload File', 'Preview', 'Processing', 'Done'];
  const stepStatus = (s) => {
    if (s < step) return 'done';
    if (s === step) return 'active';
    return '';
  };

  return (
    <div className="App">
      <style>{styles}</style>

      {/* Header */}
      <div style={{ padding: '0 0 20px 0', borderBottom: '1px solid var(--black-border)', marginBottom: 24, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={onExit} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-on-black-muted)', display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, padding: 0 }}>
            <FiArrowLeft /> Back
          </button>
          <h2 style={{ margin: 0, color: 'var(--text-on-black)' }}>
            <FiUsers style={{ marginRight: 10, verticalAlign: 'middle' }} />
            Batch Send
          </h2>
        </div>
        <span style={{ fontSize: 12, color: 'var(--text-on-black-muted)' }}>Send signing requests to multiple recipients at once</span>
      </div>

      <div className="bulk-container">
        {/* Step Indicator */}
        <div className="bulk-step-indicator">
          {stepLabels.map((label, i) => (
            <div key={i} className={`bulk-step-item ${stepStatus(i + 1)}`}>
              {step > i + 1 ? <FiCheck style={{ marginRight: 4 }} /> : null}{label}
            </div>
          ))}
        </div>

        {/* Error Banner */}
        {errorMessage && (
          <div style={{ background: '#FEE2E2', border: '1px solid #EF4444', borderRadius: 8, padding: '12px 16px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10, color: '#991B1B' }}>
            <FiAlertTriangle />
            <div style={{ flex: 1 }}>{errorMessage}</div>
            <button onClick={() => setErrorMessage('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#991B1B' }}><FiX /></button>
          </div>
        )}

        {/* ── STEP 1: Configure ─────────────────────────────────────── */}
        {step === STEPS.CONFIGURE && (
          <>
            <div className="bulk-card">
              <h3><FiLayout style={{ marginRight: 8 }} />Select Template</h3>
              <p style={{ color: 'var(--text-on-black-muted)', fontSize: 13, marginBottom: 16, marginTop: -12 }}>
                Batch send requires a template — it defines the document structure and signature positions.
              </p>
              <select className="bulk-input bulk-select" value={selectedTemplate?._id || ''} onChange={handleTemplateSelect}>
                <option value="">-- Choose a Template --</option>
                {templates.map(t => <option key={t._id} value={t._id}>{t.title}</option>)}
              </select>

              {selectedTemplate && (
                <div style={{ marginTop: 16 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-on-black-muted)', marginBottom: 6, display: 'block' }}>DOCUMENT NAME (optional override)</label>
                  <input className="bulk-input" placeholder={selectedTemplate.title} value={documentName} onChange={e => setDocumentName(e.target.value)} />
                </div>
              )}

              {/* Template text fields (shared values for all rows) */}
              {selectedTemplate?.text_fields?.length > 0 && (
                <div style={{ marginTop: 20 }}>
                  <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-on-black-secondary)', marginBottom: 12, display: 'block' }}>
                    Default Document Fields <span style={{ fontWeight: 400, color: 'var(--text-on-black-muted)' }}>(applied to all rows unless overridden in CSV)</span>
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    {[...new Set(selectedTemplate.text_fields.map(f => f.label))].map(label => (
                      <div key={label}>
                        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-on-black-muted)', marginBottom: 4, display: 'block' }}>{label}</label>
                        <input className="bulk-input" value={fieldValues[label] || ''} onChange={e => setFieldValues({ ...fieldValues, [label]: e.target.value })} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="bulk-card">
              <h3>Signing Options</h3>
              <div className="bulk-switch-row">
                <SwitchToggle checked={captureLocation} onChange={setCaptureLocation} label="Location" icon={<FiMapPin />} />
                <SwitchToggle checked={enableReminder} onChange={setEnableReminder} label="Reminder" icon={<FiClock />} />
                {enableReminder && (
                  <select value={reminderDuration} onChange={e => setReminderDuration(e.target.value)} className="bulk-input" style={{ width: 'auto', padding: '6px 10px', fontSize: 13 }}>
                    <option value="12">12h</option><option value="24">24h</option><option value="48">48h</option>
                  </select>
                )}
                <SwitchToggle checked={enableExpiry} onChange={setEnableExpiry} label="Expiry" icon={<FiCalendar />} />
                {enableExpiry && (
                  <input type="date" className="bulk-input" style={{ width: 'auto', padding: '6px 10px', fontSize: 13 }}
                    value={expiryDate} min={new Date().toISOString().split('T')[0]} onChange={e => setExpiryDate(e.target.value)} />
                )}
                <SwitchToggle checked={captureFace} onChange={setCaptureFace} label="Face Capture" icon={<FiCamera />} />
                <SwitchToggle checked={emailNotificationEnabled} onChange={setEmailNotificationEnabled} label="Email Notification" icon={<FiMail />} />
                <SwitchToggle checked={otpEnabled} onChange={setOtpEnabled} label="OTP Verification" icon={<FiShield />} />
              </div>
            </div>

            <div className="bulk-action-row">
              <button className="btn btn-secondary" onClick={onExit}>Cancel</button>
              <button className="btn btn-primary" onClick={() => {
                if (!selectedTemplate) { setErrorMessage('Please select a template first.'); return; }
                setErrorMessage('');
                setStep(STEPS.UPLOAD_CSV);
              }}>
                Next: Upload File →
              </button>
            </div>
          </>
        )}

        {/* ── STEP 2: Upload CSV ────────────────────────────────────── */}
        {step === STEPS.UPLOAD_CSV && (
          <>
            <div className="bulk-card">
              <h3><FiUploadCloud style={{ marginRight: 8 }} />Upload Recipient File</h3>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <p style={{ color: 'var(--text-on-black-muted)', fontSize: 13, margin: 0 }}>
                  Upload a CSV or Excel file. Each row = one signing request sent to one recipient.
                </p>
                <button onClick={downloadCsvTemplate}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(255,214,0,0.12)', border: '1px solid rgba(255,214,0,0.4)', borderRadius: 6, padding: '7px 14px', color: 'var(--primary)', fontWeight: 600, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  <FiDownload /> Download Template
                </button>
              </div>

              {/* Required columns info */}
              <div style={{ background: 'rgba(255,214,0,0.06)', border: '1px solid rgba(255,214,0,0.25)', borderRadius: 8, padding: 14, marginBottom: 20, fontSize: 13 }}>
                <div style={{ fontWeight: 600, marginBottom: 6, color: 'var(--text-on-black)' }}>Required columns:</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {['recipient_name', 'recipient_email'].map(col => (
                    <span key={col} style={{ background: 'rgba(255,214,0,0.15)', color: 'var(--primary)', padding: '2px 8px', borderRadius: 4, fontFamily: 'monospace', fontSize: 12 }}>{col}</span>
                  ))}
                  {['recipient_phone', 'signature_type', 'document_name'].map(col => (
                    <span key={col} style={{ background: 'var(--black-bg-card-3)', color: 'var(--text-on-black-muted)', padding: '2px 8px', borderRadius: 4, fontFamily: 'monospace', fontSize: 12 }}>{col} (optional)</span>
                  ))}
                  {selectedTemplate?.text_fields && [...new Set(selectedTemplate.text_fields.map(f => f.label))].map(col => (
                    <span key={col} style={{ background: 'rgba(16,185,129,0.1)', color: '#10B981', padding: '2px 8px', borderRadius: 4, fontFamily: 'monospace', fontSize: 12 }}>{col} (field override)</span>
                  ))}
                </div>
                <div style={{ marginTop: 8, color: 'var(--text-on-black-muted)', fontSize: 12 }}>
                  <strong>signature_type</strong> values: <code>e-signature</code>
                </div>
              </div>

              {/* Drop zone */}
              <div
                className={`bulk-drop-zone ${csvFile ? 'has-file' : ''}`}
                onDragOver={e => e.preventDefault()}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" style={{ display: 'none' }} onChange={handleFileChange} />
                {csvFile ? (
                  <div>
                    <FiCheck size={32} color="#10B981" style={{ marginBottom: 8 }} />
                    <div style={{ fontWeight: 600, color: '#10B981' }}>{csvFile.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-on-black-muted)', marginTop: 4 }}>
                      {parsedRows.length > 0 && !parsedRows[0]?._note ? `${parsedRows.length} rows detected` : 'Click to change file'}
                    </div>
                  </div>
                ) : (
                  <div>
                    <FiUploadCloud size={36} style={{ marginBottom: 12, opacity: 0.5 }} />
                    <div style={{ fontWeight: 600, fontSize: 15 }}>Drop CSV or Excel file here</div>
                    <div style={{ fontSize: 13, marginTop: 6 }}>or click to browse — .csv, .xlsx, .xls supported</div>
                  </div>
                )}
              </div>

              {parseError && (
                <div style={{ color: '#f87171', fontSize: 13, marginTop: 10, display: 'flex', gap: 6, alignItems: 'center' }}>
                  <FiAlertTriangle /> {parseError}
                </div>
              )}
            </div>

            <div className="bulk-action-row">
              <button className="btn btn-secondary" onClick={() => setStep(STEPS.CONFIGURE)}>← Back</button>
              <button className="btn btn-primary" onClick={() => {
                if (!csvFile) { setErrorMessage('Please upload a file.'); return; }
                if (parseError) { setErrorMessage(parseError); return; }
                setErrorMessage('');
                setStep(STEPS.PREVIEW);
              }}>
                Preview →
              </button>
            </div>
          </>
        )}

        {/* ── STEP 3: Preview ───────────────────────────────────────── */}
        {step === STEPS.PREVIEW && (
          <>
            <div className="bulk-card">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <h3 style={{ margin: 0 }}>Preview — {parsedRows.length} Requests</h3>
                <div style={{ display: 'flex', gap: 8, fontSize: 13, color: 'var(--text-on-black-muted)', alignItems: 'center' }}>
                  Template: <strong style={{ color: 'var(--primary)' }}>{selectedTemplate?.title}</strong>
                </div>
              </div>

              {parsedRows[0]?._note ? (
                <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-on-black-muted)' }}>
                  <FiLayout size={32} style={{ marginBottom: 12, opacity: 0.4 }} />
                  <div>Excel preview not available client-side.</div>
                  <div style={{ fontSize: 12, marginTop: 4 }}>File will be processed on submission.</div>
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table className="bulk-preview-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        {Object.keys(parsedRows[0] || {}).slice(0, 6).map(k => <th key={k}>{k}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {parsedRows.slice(0, 20).map((row, i) => (
                        <tr key={i}>
                          <td style={{ color: 'var(--text-on-black-muted)' }}>{i + 1}</td>
                          {Object.keys(parsedRows[0]).slice(0, 6).map(k => (
                            <td key={k}>{row[k] || <span style={{ color: 'var(--text-on-black-muted)', fontStyle: 'italic' }}>—</span>}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {parsedRows.length > 20 && (
                    <div style={{ textAlign: 'center', padding: '12px 0', fontSize: 13, color: 'var(--text-on-black-muted)' }}>
                      … and {parsedRows.length - 20} more rows
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Summary */}
            <div className="bulk-card" style={{ display: 'flex', gap: 20, flexWrap: 'wrap', padding: 20 }}>
              <div style={{ flex: 1, minWidth: 160 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-on-black-muted)', marginBottom: 6, textTransform: 'uppercase' }}>Template</div>
                <div style={{ fontWeight: 600 }}>{selectedTemplate?.title}</div>
              </div>
              <div style={{ flex: 1, minWidth: 160 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-on-black-muted)', marginBottom: 6, textTransform: 'uppercase' }}>Total Requests</div>
                <div style={{ fontWeight: 700, fontSize: 22, color: 'var(--primary)' }}>{parsedRows.length}</div>
              </div>
              <div style={{ flex: 1, minWidth: 160 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-on-black-muted)', marginBottom: 6, textTransform: 'uppercase' }}>Email Notifications</div>
                <div style={{ fontWeight: 600, color: emailNotificationEnabled ? '#10B981' : 'var(--text-on-black-muted)' }}>
                  {emailNotificationEnabled ? 'Enabled' : 'Disabled'}
                </div>
              </div>
            </div>

            <div className="bulk-action-row">
              <button className="btn btn-secondary" onClick={() => setStep(STEPS.UPLOAD_CSV)}>← Back</button>
              <button className="btn btn-primary" disabled={isSubmitting} onClick={handleSubmit}>
                {isSubmitting ? 'Starting…' : `Send ${parsedRows.length} Requests →`}
              </button>
            </div>
          </>
        )}

        {/* ── STEP 4: Processing ────────────────────────────────────── */}
        {step === STEPS.PROGRESS && batchStatus && (
          <div className="bulk-card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <h3 style={{ margin: 0 }}>Processing Batch…</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-on-black-muted)' }}>
                <FiRefreshCw size={14} style={{ animation: 'spin 1.5s linear infinite' }} /> Live updates
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--text-on-black-muted)', marginBottom: 4 }}>
                <span>{batchStatus.processed} of {batchStatus.total} processed</span>
                <span>{progressPct}%</span>
              </div>
              <div className="bulk-progress-bar-track">
                <div className="bulk-progress-bar-fill" style={{ width: `${progressPct}%` }} />
              </div>
            </div>

            <div className="bulk-stat">
              <div className="bulk-stat-item">
                <div className="bulk-stat-num" style={{ color: 'var(--text-on-black)' }}>{batchStatus.total}</div>
                <div className="bulk-stat-label">Total</div>
              </div>
              <div className="bulk-stat-item">
                <div className="bulk-stat-num" style={{ color: '#10B981' }}>{batchStatus.success}</div>
                <div className="bulk-stat-label">Sent</div>
              </div>
              <div className="bulk-stat-item">
                <div className="bulk-stat-num" style={{ color: '#f87171' }}>{batchStatus.failed}</div>
                <div className="bulk-stat-label">Failed</div>
              </div>
              <div className="bulk-stat-item">
                <div className="bulk-stat-num" style={{ color: 'var(--text-on-black-muted)' }}>{batchStatus.total - batchStatus.processed}</div>
                <div className="bulk-stat-label">Pending</div>
              </div>
            </div>

            {batchStatus.errors?.length > 0 && (
              <div className="bulk-error-list">
                <div style={{ fontWeight: 600, fontSize: 12, color: '#f87171', marginBottom: 8 }}>Errors:</div>
                {batchStatus.errors.map((e, i) => (
                  <div key={i} className="bulk-error-row">Row {e.row}: {e.error}</div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── STEP 5: Done ──────────────────────────────────────────── */}
        {step === STEPS.DONE && batchStatus && (
          <div className="bulk-card" style={{ textAlign: 'center', padding: 48 }}>
            <div style={{ fontSize: 56, marginBottom: 16 }}>
              {batchStatus.failed === 0 ? '🎉' : '⚠️'}
            </div>
            <h2 style={{ color: 'var(--text-on-black)', marginBottom: 8 }}>
              Batch Complete
            </h2>
            <p style={{ color: 'var(--text-on-black-muted)', marginBottom: 24 }}>
              {batchStatus.success} of {batchStatus.total} requests sent successfully.
              {batchStatus.failed > 0 && ` ${batchStatus.failed} failed.`}
            </p>

            <div className="bulk-stat" style={{ justifyContent: 'center', marginBottom: 28 }}>
              <div className="bulk-stat-item" style={{ minWidth: 120 }}>
                <div className="bulk-stat-num" style={{ color: '#10B981' }}>{batchStatus.success}</div>
                <div className="bulk-stat-label">Sent</div>
              </div>
              {batchStatus.failed > 0 && (
                <div className="bulk-stat-item" style={{ minWidth: 120 }}>
                  <div className="bulk-stat-num" style={{ color: '#f87171' }}>{batchStatus.failed}</div>
                  <div className="bulk-stat-label">Failed</div>
                </div>
              )}
            </div>

            {batchStatus.errors?.length > 0 && (
              <div className="bulk-error-list" style={{ textAlign: 'left', marginBottom: 24 }}>
                <div style={{ fontWeight: 600, fontSize: 12, color: '#f87171', marginBottom: 8 }}>Row errors:</div>
                {batchStatus.errors.map((e, i) => (
                  <div key={i} className="bulk-error-row">Row {e.row}: {e.error}</div>
                ))}
              </div>
            )}

            <button className="btn btn-primary" onClick={onExit}>Back to Dashboard</button>
          </div>
        )}
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export default BulkRequest;
